import React, { useState } from 'react';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import { Box, Card, CardContent, CardMedia, CircularProgress, Typography, Grid, Theme, useMediaQuery } from '@mui/material';
import { formatDate } from '../utils';
import { RoleType } from '../types';
import data from '../data.json';
import './styles.css';

function Experience() {
    const [selected, setSelected] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const timeLineItem = (role: RoleType, index: number) => (
        <TimelineItem key={role.id} className={'raise-timeline'}>
          <TimelineSeparator>
            <TimelineDot color={index % 2 === 0 ? "secondary" : "primary"} />
            {index !== data.experience[selected].roles.length - 1 && <TimelineConnector />}
          </TimelineSeparator>
          <TimelineContent>
            <Typography
              variant="h6"
              sx={{
                fontSize: {
                  xs: '14px',
                  sm: '16px',
                  md: '18px',
                },
              }}
            >
              {role.role}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: {
                  xs: '12px',
                  sm: '14px',
                  md: '16px',
                },
              }}
            >
              {formatDate(new Date(role.startDate))} - {formatDate(new Date(role.endDate))}
            </Typography>
            <ul>
              {role.project.description.split('.').map((point, pointIndex) => (
                point.trim() !== "" && (
                  <li
                    key={`${role.id}-${pointIndex}`}
                    style={{
                      fontSize: isSmallScreen ? '13px' : '16px', // Adjust size for mobile
                    }}
                  >
                    {point}
                  </li>
                )
              ))}
            </ul>
          </TimelineContent>
        </TimelineItem>
      );
      

    const timelineItems = (roles: any) =>
        roles.map((role: RoleType, index: number) => timeLineItem(role, index));

    return (
        <Box>
            {!loading ? (
                <Grid container spacing={3} sx={{ width: '100%' }}>
                    {/* Sidebar (Projects List) */}
                    <Grid
                        item
                        xs={12}
                        md={4}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: isSmallScreen ? 'center' : 'flex-start',
                        }}
                    >
                        {data.experience.map((project, idx) => (
                            <Card
                                key={idx}
                                onClick={() => setSelected(idx)}
                                sx={{
                                    maxWidth: isSmallScreen ? '100%' : 345,
                                    margin: '10px',
                                    cursor: 'pointer',
                                    boxShadow: selected === idx ? '0px 0px 5px rgb(162, 179, 197)' : 'none',
                                    border: selected === idx ? '0.5px solid rgb(164, 176, 188)' : 'none',
                                }}
                            >
                                <CardMedia
                                    sx={{ height: 150 }}
                                    image={
                                        project.company === 'ZeMoSo Technologies'
                                            ? require('../assets/zemoso.png')
                                            : require('../assets/fam.png')
                                    }
                                    title={project.company}
                                    onClick={() => project.link && window.open(project.link, '_blank')}
                                />
                                <CardContent>
                                    <Typography variant="h6">{project.company}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {project.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </Grid>

                    <Grid item xs={12} md={8} sx={{ padding: isSmallScreen ? '10px' : '20px' }}>
                        {data.experience[selected].roles.length > 1 && !isSmallScreen ? (
                            <Timeline position={isSmallScreen ? 'right' : 'alternate-reverse'}>
                            {timelineItems(data.experience[selected].roles)}
                            </Timeline>
                        ) : (
                          data.experience[selected].roles.map((role, roleIndex) => (
                            <Box key={roleIndex} sx={{ marginBottom: '16px' }}>
                              {/* Role Title */}
                              <Typography
                                variant="h6"
                                sx={{ fontSize: isSmallScreen ? '20px' : '24px', marginBottom: '8px' }}
                              >
                                {role.role}
                              </Typography>
                        
                              {/* Role Date Range */}
                              <Typography
                                variant="subtitle1"
                                sx={{ fontSize: isSmallScreen ? '14px' : '18px', marginBottom: '8px' }}
                              >
                                {formatDate(new Date(role.startDate))} -{' '}
                                {formatDate(new Date(role.endDate))}
                              </Typography>
                        
                              {/* Project Description */}
                              <Box
                                component="ul"
                                sx={{
                                  textAlign: 'left',
                                  paddingLeft: isSmallScreen ? '16px' : '24px',
                                }}
                              >
                                {role.project.description.split('.').map((point, index) => (
                                  point.trim() !== '' && (
                                    <Typography
                                      key={index}
                                      component="li"
                                      variant="body2"
                                      sx={{ fontSize: isSmallScreen ? '14px' : '16px', marginBottom: '6px' }}
                                    >
                                      {point.trim()}
                                    </Typography>
                                  )
                                ))}
                              </Box>
                            </Box>
                          ))
                        )}
                    </Grid>
                </Grid>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
}

export default Experience;
